import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { api } from "../api/endpoints";
import { ApiError, errorMessage } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { KeyReturnIcon } from "@phosphor-icons/react";
import { Button } from "../components/Button";
import { TerminalInput } from "../components/TerminalInput";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useNow } from "../lib/useNow";
import { useTyped } from "../hud/useTyped";
import { HoloRings } from "../scene/HoloRings";

// Sign-in from the approved mockup: the badge in thin white rotating rings,
// then a terminal. "> identify" and "> passcode" type out on load, the
// passcode shows real asterisks with a blinking block cursor, and the real
// inputs underneath keep autofill and password managers working.

const clockFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

function finePointer(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;
}

/** The rings-and-badge hologram plus the terminal frame, shared by sign-in and set-password. */
export function TerminalScreen({ header, children }: { header: string; children: ReactNode }) {
  const now = useNow();
  return (
    <div className="auth">
      <div className="hologram hologram--login" aria-hidden="true">
        <HoloRings variant="login" idPrefix="login" />
        <div className="badge badge--login">
          <img
            src="/brand/logo-640.webp"
            srcSet="/brand/logo-320.webp 320w, /brand/logo-640.webp 640w, /brand/logo-960.webp 960w"
            sizes="(min-width: 700px) 210px, 40vw"
            width={210}
            height={210}
            alt=""
            decoding="async"
            fetchPriority="high"
          />
        </div>
      </div>
      <div className="term">
        <div className="term__hdr" aria-hidden="true">
          <span>{header}</span>
          <span>{clockFmt.format(now)}</span>
        </div>
        {children}
      </div>
    </div>
  );
}

/** "> identify": the red prompt and the typed-out key. */
export function Prompt({ text }: { text: string }) {
  return (
    <>
      <span className="p">&gt;</span>
      <span className="k">{text}</span>
    </>
  );
}

export function AuthPage({ notice }: { notice?: string }) {
  useDocumentTitle("Sign in");
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uplinkDown, setUplinkDown] = useState(false);
  const typed = useTyped(["border rangers // secure uplink", "identify", "passcode"], 32, 200);
  const [hdr = "", id = "", pw = ""] = typed.shown;

  // Real server health: only mentioned when it is down.
  useEffect(() => {
    const ctrl = new AbortController();
    api
      .health(ctrl.signal)
      .then((h) => setUplinkDown(!h.ok))
      .catch(() => {
        if (!ctrl.signal.aborted) setUplinkDown(true);
      });
    return () => ctrl.abort();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(!email.trim() ? "identify yourself: enter your email." : "enter your passcode.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(email.trim(), password);
      signIn(res);
      navigate(from && from !== "/" && from !== "/signup" ? from : "/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? "access denied. email or passcode is wrong. no account? ask the owner to add you." : errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <TerminalScreen header={hdr}>
      <form className="term__form" onSubmit={submit} noValidate aria-labelledby="signin-title">
        <h1 id="signin-title" className="sr-only">
          Border Rangers sign in
        </h1>
        {notice ? (
          <p className="term__note" role="status">
            # {notice.toLowerCase()}
          </p>
        ) : null}
        <TerminalInput
          prompt={<Prompt text={id} />}
          label="Email"
          type="email"
          name="email"
          inputMode="email"
          autoComplete="username"
          autoFocus={finePointer()}
          idleCursor={!email}
          required
          value={email}
          onChange={setEmail}
          invalid={!!error && !email.trim()}
        />
        <TerminalInput
          prompt={<Prompt text={pw} />}
          label="Password"
          masked
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={setPassword}
          invalid={!!error && !!email.trim() && !password}
        />
        {error ? (
          <p className="term__err" role="alert">
            ! {error}
          </p>
        ) : null}
        <div className="enter">
          <Button type="submit" variant="primary" icon={KeyReturnIcon} block disabled={busy}>
            {busy ? "authenticating…" : "authenticate"}
          </Button>
        </div>
        {uplinkDown ? (
          <p className="term__note warn-text" role="status">
            # uplink unreachable. the server is not answering.
          </p>
        ) : null}
      </form>
    </TerminalScreen>
  );
}
